import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { configService } from '@/config'
import { log } from '@/utils/logger'

/**
 * Epoch Monitor Hook
 *
 * Monitors blockchain epoch changes and automatically invalidates
 * epoch-dependent queries to ensure fresh data.
 *
 * Epochs on X1 change approximately every 2 days.
 * When an epoch change is detected:
 * - Vote accounts data is invalidated
 * - Leader schedule is invalidated
 * - Epoch info is invalidated
 * - Validator staking data is refreshed
 *
 * Usage: Call this hook once at the app root level (App.tsx)
 */
export const useEpochMonitor = () => {
  const queryClient = useQueryClient()
  const lastEpochRef = useRef<number | null>(null)

  useEffect(() => {
    const checkEpoch = async () => {
      try {
        const network = configService.getCurrentNetwork()

        const response = await fetch(`/api/rpc/epoch-info?network=${network}`)

        if (!response.ok) {
          log.warn('Failed to fetch epoch info for monitoring')
          return
        }

        const data = await response.json()
        const currentEpoch = data.epoch

        // Check if epoch has changed
        if (lastEpochRef.current !== null && currentEpoch !== lastEpochRef.current) {
          log.info(`Epoch changed from ${lastEpochRef.current} to ${currentEpoch}`)

          // Invalidate epoch-dependent queries
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['epoch-info'] }),
            queryClient.invalidateQueries({ queryKey: ['vote-accounts'] }),
            queryClient.invalidateQueries({ queryKey: ['leader-schedule'] }),
            queryClient.invalidateQueries({ queryKey: ['validators'] }), // Staking data changes
          ])

          log.info('Epoch-dependent queries invalidated')
        }

        lastEpochRef.current = currentEpoch
      } catch (error) {
        log.warn('Epoch check failed:', error)
      }
    }

    // Initial check
    checkEpoch()

    // Check every 5 minutes (epochs change slowly, so we don't need frequent checks)
    const interval = setInterval(checkEpoch, 5 * 60 * 1000)

    return () => {
      clearInterval(interval)
    }
  }, [queryClient])

  // Return current epoch for external use if needed
  return {
    currentEpoch: lastEpochRef.current,
  }
}
